import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { toString } from 'lodash';
import queryString from 'query-string';
import { useLocation } from 'react-router-dom';
import { AllocationName } from 'api';
import { useStore } from 'common/store';
import { CapTableVersion } from 'common/types/company';
import { MeasurementDate } from 'common/types/measurementDate';
import { UseStoreValues } from 'common/types/store';
import { ValuationByAllocation } from 'common/types/valuation';
import { CompaniesService } from 'services';
import { useGetCapTableVersionsByCompanyAndMD } from 'services/hooks/companies';
import { useGetValuationByAllocation } from 'services/hooks/valuations';

interface UseDialogValuationInfoParams {
  enabled: boolean;
  // Only the Cap Table and Breakpoints options consume the cap table versions, so
  // exports that leave both unchecked skip the request and its loading gate.
  needsCapTableVersions: boolean;
}

interface UseDialogValuationInfoResult {
  valuationInfo: ValuationByAllocation | undefined;
  isLoading: boolean;
  // Resolved calendar date (YYYY-MM-DD) of the measurement date the export targets.
  // The dialog uses this for the cover/filename when the URL has no `date` param
  // (e.g. Prism's "Most Recent" selection clears it).
  measurementDate: string | undefined;
  // The allocation version this export targets. Carries the is_final / last_finalized_at
  // stamp the dialog defaults the cover issue date from, which AllocationValuation
  // (valuationInfo.allocation) does not expose.
  selectedAllocationVersion: AllocationName | undefined;
  // Cap table versions for the targeted measurement date. Resolves a referenced id to a
  // name (page title) and slug (`?version=`).
  capTableVersions: CapTableVersion[] | null | undefined;
}

const PDF_EXPORT_DIALOG_MEASUREMENT_DATES_QUERY_KEY = 'pdf-export-dialog-measurement-dates';
const PDF_EXPORT_DIALOG_ALLOCATION_VERSIONS_QUERY_KEY = 'pdf-export-dialog-allocation-versions';

const pickStringParam = (value: string | (string | null)[] | null | undefined): string | undefined =>
  (typeof value === 'string' && value !== '' && value !== 'undefined' ? value : undefined);

export const useDialogValuationInfo = (
  { enabled, needsCapTableVersions }: UseDialogValuationInfoParams
): UseDialogValuationInfoResult => {
  const location = useLocation();
  const [{ companyInfo }] = useStore() as unknown as UseStoreValues;
  const companyId = companyInfo?.id as number | undefined;

  const { dateSlug, versionSlug } = useMemo(() => {
    const parsed = queryString.parse(location.search);
    return {
      dateSlug: pickStringParam(parsed.date),
      versionSlug: pickStringParam(parsed.version),
    };
  }, [location.search]);

  // Step 1: load the company's measurement dates so the URL `date` slug can resolve to an id.
  const { data: measurementDates } = useQuery({
    enabled: enabled && Boolean(companyId),
    queryKey: [PDF_EXPORT_DIALOG_MEASUREMENT_DATES_QUERY_KEY, companyId],
    queryFn: (): Promise<MeasurementDate[]> => new CompaniesService().measurementDates(companyId as number),
  });

  const measurementDateId = useMemo(() => {
    if (!measurementDates?.length) {
      return null;
    }
    // The Prism nav writes the plain calendar date to ?date= (not the company-specific
    // slug) and clears it entirely for "Most Recent". Match on slug/date/id, and fall
    // back to the most recent measurement date (first in the list) the same way the
    // company pages do (useCompanyMeasurementDateFilter), so approach options resolve
    // even when the URL has no explicit — or a plain-date — `date` param.
    if (!dateSlug) {
      return measurementDates[0]?.id ?? null;
    }
    const matched = measurementDates.find(
      md => md.slug === dateSlug || md.date === dateSlug || toString(md.id) === dateSlug
    );
    return (matched ?? measurementDates[0])?.id ?? null;
  }, [measurementDates, dateSlug]);

  // Resolve the matched measurement date's calendar date so the cover/filename
  // can show it even when the URL omits the `date` param.
  const resolvedMeasurementDate = useMemo<string | undefined>(
    () => measurementDates?.find(md => md.id === measurementDateId)?.date,
    [measurementDates, measurementDateId]
  );

  // Step 2: load allocation versions for the matched company + measurement date.
  const { data: allocationVersions } = useQuery({
    enabled: enabled && Boolean(companyId) && Boolean(measurementDateId),
    queryKey: [PDF_EXPORT_DIALOG_ALLOCATION_VERSIONS_QUERY_KEY, companyId, measurementDateId],
    queryFn: (): Promise<AllocationName[]> =>
      new CompaniesService().getAllocationsVersionsByCompanyIdByDateId(
        toString(companyId),
        toString(measurementDateId)
      ) as Promise<AllocationName[]>,
  });

  // Step 3: pick the allocation that matches the URL `version` slug, otherwise fall back the same way
  // the merged Valuations page does (final → published → first available).
  const selectedAllocationVersion = useMemo<AllocationName | undefined>(() => {
    if (!allocationVersions?.length) {
      return undefined;
    }
    const matched = versionSlug ? allocationVersions.find(v => v.slug === versionSlug) : undefined;
    return (
      matched
      ?? allocationVersions.find(v => v.is_final)
      ?? allocationVersions.find(v => v.is_published)
      ?? allocationVersions[0]
    );
  }, [allocationVersions, versionSlug]);

  // Step 3b: load the cap table versions for the same measurement date.
  const { data: capTableVersions, isLoading: isLoadingCapTableVersions } = useGetCapTableVersionsByCompanyAndMD({
    company: companyInfo,
    measurementDate: measurementDateId ? { md_id: measurementDateId } : null,
    shouldQueryAutomatically: enabled && needsCapTableVersions && Boolean(measurementDateId),
  });

  const allocationId = selectedAllocationVersion?.id ?? undefined;

  // Step 4: fetch the merged valuation. Like step 3b, reuses the app's existing hook so the
  // cache is shared with the Valuations page — opening the dialog there hits it, not the network.
  const { data: valuationInfo, isLoading: isLoadingValuation } = useGetValuationByAllocation({
    allocationId,
    shouldQueryAutomatically: enabled && Boolean(allocationId),
  });

  const isLoading = useMemo(() => {
    if (!enabled || !companyId) {
      return false;
    }
    if (!measurementDates) {
      return true;
    }
    if (measurementDateId && !allocationVersions) {
      return true;
    }
    // Holds the export button while the versions load — starting sooner would build the page
    // list from the single-page fallback and silently drop the other versions.
    if (needsCapTableVersions && isLoadingCapTableVersions) {
      return true;
    }
    if (allocationId && isLoadingValuation) {
      return true;
    }
    return false;
  }, [
    enabled,
    companyId,
    measurementDates,
    measurementDateId,
    allocationVersions,
    needsCapTableVersions,
    isLoadingCapTableVersions,
    allocationId,
    isLoadingValuation,
  ]);

  return {
    valuationInfo: valuationInfo as ValuationByAllocation | undefined,
    isLoading,
    measurementDate: resolvedMeasurementDate,
    selectedAllocationVersion,
    capTableVersions,
  };
};

export default useDialogValuationInfo;
